/**
 * `<NotesTab>` — orchestrator for the Notes tab content (Batch 30j-6).
 *
 * The fifth and final sibling pane in `<CandidatePage>`. Two visual
 * states (empty / populated) plus loading + error sub-states.
 *
 * Architect refs:
 *   - `frontend/docs/30j_6_design.md` §3.1 (composition)
 *   - `frontend/docs/30j_6_architect.md` §3.1 (orchestrator pattern) +
 *     §5.4 (errorKey rollback signal — AR-11)
 *
 * Render-and-hide canon: the `useNotes` query is gated on
 * `activeTab === "notes"`. When the user is on a different tab, the
 * queryFn is NEVER invoked (LCP guarantee).
 *
 * Append-only invariant (HoD §10 + Sec HARD-7): NO `useDeleteNote` /
 * `useUpdateNote` hooks exist; the only mutation is `useCreateNote`.
 *
 * NOTE on file-local sub-components (Architect §14 ts-prune anti-
 * pattern): `<NotesListSkeleton>` and `<NotesListError>` are defined
 * here without `export` — they are file-local, consumed only inside
 * `<NotesTab>`. Adding `export` would trip ts-prune.
 */
import { useMemo, useState, type JSX } from "react";
import { AlertTriangle } from "lucide-react";

import type { Candidate } from "@/hooks/queries/candidates/useCandidates";
import type { TabKey } from "@/components/candidate-page/tabs";
import { Skeleton } from "@/components/common/Skeleton";
import { NoteComposer } from "@/components/candidate-page/NoteComposer";
import { NotesList } from "@/components/candidate-page/NotesList";
import { NotesEmptyState } from "@/components/candidate-page/NotesEmptyState";
import { useNotes, type NoteOut } from "@/hooks/queries/notes/useNotes";
import { useCreateNote } from "@/hooks/queries/notes/useCreateNote";

interface NotesTabProps {
  candidate: Candidate;
  /** Currently active tab — drives `useNotes`'s enabled gate. */
  activeTab: TabKey;
  /** Phase 2.5: JD context for per-(candidate × JD) note scoping. */
  jdId?: string;
  /** JD title for the empty-state copy. */
  jdTitle?: string;
}

function NotesListSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3" data-testid="notes-list-skeleton">
      {[0, 1, 2].map((i) => (
        <article
          key={i}
          className="rounded-md border border-border-default bg-surface-card p-4"
        >
          <header className="mb-2 flex items-center gap-2">
            <Skeleton height={16} width={64} />
            <Skeleton height={16} width={96} />
          </header>
          <div className="flex flex-col gap-1">
            <Skeleton height={16} width="100%" />
            <Skeleton height={16} width="85%" />
            <Skeleton height={16} width="60%" />
          </div>
        </article>
      ))}
    </div>
  );
}

function NotesListError({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div
      className="flex items-start gap-3 rounded-md border border-status-rejected-ring bg-status-rejected-bg p-3.5 text-status-rejected-fg"
      data-testid="notes-list-error"
    >
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden />
      <div className="flex-1">
        <p className="mb-0.5 text-sm font-semibold">Couldn&apos;t load notes</p>
        <p className="text-xs">Check your connection and try again.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1.5 text-xs underline"
          data-testid="notes-list-error-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function NotesTab({ candidate, activeTab, jdId, jdTitle }: NotesTabProps): JSX.Element {
  const notesQ = useNotes({
    candidateId: candidate.id,
    enabled: activeTab === "notes",
  });
  const createNote = useCreateNote();

  // Bump on each mutation error so the composer's useEffect detects a
  // NEW error vs. the last one it handled (AR-11).
  const [errorKey, setErrorKey] = useState<number>(0);

  const flatRows = useMemo<NoteOut[]>(
    () => notesQ.data?.pages.flatMap((p) => p.items) ?? [],
    [notesQ.data],
  );

  const hasNotes = flatRows.length > 0;
  const isInitialLoading = notesQ.isLoading;
  const isError = notesQ.isError;

  // Phase 2.5: pipe jdId through so notes are per-(candidate × JD).
  const handleSubmit = (content: string, resolvedJdId?: string): void => {
    createNote.mutate(
      { candidate_id: candidate.id, content, jd_id: resolvedJdId },
      {
        onError: () => setErrorKey((k) => k + 1),
      },
    );
  };

  return (
    <div
      className="scroll-primary flex flex-1 flex-col overflow-x-hidden overflow-y-auto"
      data-testid="notes-tab"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-content-px py-5">
        <NoteComposer
          onSubmit={handleSubmit}
          isPending={createNote.isPending}
          isError={createNote.isError}
          errorKey={errorKey}
          jdId={jdId}
        />
        {isInitialLoading ? (
          <NotesListSkeleton />
        ) : isError ? (
          <NotesListError onRetry={() => void notesQ.refetch()} />
        ) : hasNotes ? (
          <NotesList
            rows={flatRows}
            hasNextPage={!!notesQ.hasNextPage}
            isFetchingNextPage={notesQ.isFetchingNextPage}
            onLoadMore={() => void notesQ.fetchNextPage()}
          />
        ) : (
          <NotesEmptyState candidateName={candidate.name} jdTitle={jdTitle} />
        )}
      </div>
    </div>
  );
}
